import {
  EMPTY_STRING,
  inventoryConstants,
  toggleSwitchButtonConstants,
} from "../../../constants";
import {
  PRODUCT_TYPES_ENUMS,
  TRADING_CARD_CATEGORIES_ENUMS,
} from "../../../system/globalEnums";
import {
  batchCostOfGoodsChange,
  batchStockPriceChange,
  getCogsValue,
  getDisplayProductTagCount,
  getDisplayProductTags,
  getProductImage,
  getQuantityValue,
  handleBatchProductDelete,
  handleCalculateEachProductPercentage,
  handleValueUpdateInBatchModalRow,
  isStoreCustomProduct,
  onChangeProductQuantity,
} from "../inventoryUtility";
import {
  getInvStockPrice,
  getActionColumnOfReactDataTableComponent,
  getProductIfExist,
  getProductSubCategory,
  getShortestSku,
  handleLockOrUnlockTradeItem,
  handleShowNintendoCondition,
  isMarketPriceHigh,
  isSystemProduct,
  onChangeProductCondition,
  parseToDecimalNumber,
  parseToThousandSeperatorDecimalNumber,
} from "../../utility";
import {
  getTradingCardConditionListForSelectedGrade,
  getTradingCardMarketPriceForSelectedProperties,
} from "../thirdPartyProductsApiUtility";
import {
  DeleteIcon,
  EditIcon,
  LockIcon,
  UnLockIcon,
} from "../../../assets/icons/iconsProvider";
import {
  SelectGrade,
  TitleToolTip,
  RotatingLines,
  SelectDropDown,
  SelectCardRarity,
  TableCustomHeader,
  SimpleNumberField,
  CustomCellRendererWithSkeleton,
} from "../../components";
import ProductImageCard from "../../components/productDetailCard/ProductImageCard";

const getSkuColumnWithImage = () => {
  return {
    headerName: "Sku",
    lockPosition: "left",
    field: "sku",
    cellStyle: {},
    minWidth: 180,
    cellRenderer: (row) => {
      const shortestSku = getShortestSku(row.data.sku);
      return (
        <CustomCellRendererWithSkeleton
          params={row}
          componentRenderCondition={row?.data?.sku}
          cellComponent={
            <div className="d-flex justify-content-start align-items-center">
              <div className="product-image">
                <ProductImageCard
                  src={getProductImage(row.data)}
                  alt={`${
                    row.data.productName ||
                    row.data.product_name ||
                    "Product image"
                  }`}
                />
              </div>
              <TitleToolTip title={row.data.sku} children={shortestSku} />
            </div>
          }
        />
      );
    },
    checkboxSelection: true,
    headerCheckboxSelection: true,
  };
};

const getProductNameColumn = (handleEditClick = () => {}) => {
  return {
    headerTooltip: "Product Name",
    headerName: "Product Name",
    field: "product_name",
    maxWidth: undefined,
    cellRenderer: (params) => (
      <CustomCellRendererWithSkeleton
        params={params}
        titleToolTipText={params?.data?.product_name}
        componentRenderCondition={params?.data?.product_name}
        cellComponent={
          <span
            className=" product-edit-anchor-link cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick(params.data);
            }}
          >
            {params?.data?.product_name}
          </span>
        }
      />
    ),
  };
};

const getProductTypeColumn = (field = "productType") => {
  return {
    headerName: "Type",
    field: field,
    cellRenderer: (row) => {
      const value =
        row?.data?.productType || // inventory/items, inventory/priceChanges
        row?.data?.product?.productType; // inventory/history
      return (
        <CustomCellRendererWithSkeleton
          params={row}
          componentRenderCondition={value}
          cellComponent={value}
        />
      );
    },
  };
};

const getProductCategoryColumn = (field = "category_name") => {
  return {
    headerName: "Category",
    field: field,
    cellRenderer: (params) => {
      const value =
        params?.data?.category_name || // inventory/items, inventory/priceChanges
        params?.data?.product?.category; // inventory/history
      return (
        <CustomCellRendererWithSkeleton
          params={params}
          componentRenderCondition={value}
          cellComponent={value}
        />
      );
    },
  };
};

const getProductConditionColumn = (field = "price.type") => {
  return {
    headerName: "Condition",
    field: field,
    cellRenderer: (params) => {
      const value =
        params?.data?.price?.type || // inventory/items, inventory/priceChanges
        params?.data?.product?.price?.type; // inventory/history

      return (
        <CustomCellRendererWithSkeleton
          params={params}
          componentRenderCondition={value}
          cellComponent={value}
        />
      );
    },
  };
};

const getProductTagColumn = (field = "tags", isAgGrid = true, props) => {
  let column = {};

  if (isAgGrid) {
    column = {
      headerName: "Tags",
      field: field,
      cellRenderer: (params) => {
        const value = getDisplayProductTags(
          params?.data?.tags || // inventory/items, inventory/priceChanges
            params?.data?.product?.tags // inventory/history
        );
        return (
          <CustomCellRendererWithSkeleton
            params={params}
            componentRenderCondition={params.id}
            cellComponent={value?.length}
            titleToolTipText={value}
          />
        );
      },
    };
  } else {
    column = {
      id: "tags",
      name: "Tags",
      selector: (row) => row.tags,
      cell: (row) => {
        const value = getDisplayProductTags(row?.tags);
        return (
          <CustomCellRendererWithSkeleton
            componentRenderCondition={true}
            cellComponent={
              row.isReturn === false ? EMPTY_STRING : value?.length
            }
            titleToolTipText={value}
          />
        );
      },
      sortable: true,
      sortDirection: null,
      ...props,
    };
  }
  return column;
};

// Same column used in inventory, invoice and trade modals
const getBatchModalGradeColumn = (
  addBatchInventory,
  setBatchInventory,
  storeId,
  markupPresets,
  tradeinMargin,
  isTradeBatch
) => {
  // Only render grade column if there's one product with product type trading card (return false when don't want to render, as column array is filtered before passing into table component (explore getFormattedColDefs, excludeNullColumns))
  return (
    addBatchInventory.some(
      (product) => product.productType === PRODUCT_TYPES_ENUMS.TRADING_CARD
    ) && {
      id: "grade",
      name: "Grade",
      selector: (row) => row.grade,
      cell: (row) => {
        return (
          <SelectGrade
            isFormik={false}
            value={row.grade}
            priceChartingProductInfo={row}
            setFieldValue={(...e) => {
              // handle update grade, condition list based on selected grade, default selected condition and setBatch in reducer
              let conditionList = getTradingCardConditionListForSelectedGrade(
                e[1],
                row.subcategory
              );
              handleValueUpdateInBatchModalRow(
                row,
                ["grade", "productConditionList", "productCondition"],
                [e[1], conditionList, conditionList[0].value],
                addBatchInventory,
                setBatchInventory,
                storeId,
                markupPresets,
                tradeinMargin,
                isTradeBatch
              );
            }}
          />
        );
      },
      sortable: false,
      sortDirection: null,
      width: "8%",
    }
  );
};

// Same column used in inventory and trade modals
const getBatchModalRarityColumn = (
  addBatchInventory,
  setAddBatchInventory,
  storeId,
  markupPresets,
  tradeinMargin
) => {
  const handleOnChangeCardRarityInBatchModalRow = (e, row) => {
    const cardRarityValue = e[1];
    // Update cardRarity, marketPrice
    const marketPrice = getTradingCardMarketPriceForSelectedProperties(
      row,
      undefined,
      undefined,
      cardRarityValue
    );
    handleValueUpdateInBatchModalRow(
      row,
      ["cardRarity", "marketPrice"],
      [cardRarityValue, marketPrice],
      addBatchInventory,
      setAddBatchInventory,
      storeId
    );
  };

  // Only render Printing column if there's one product with product type trading card  (return false when don't want to render, as column array is filtered before passing into table component (explore getFormattedColDefs, excludeNullColumns))
  return (
    addBatchInventory.some(
      (product) => product.productType === PRODUCT_TYPES_ENUMS.TRADING_CARD
    ) && {
      id: "cardRarity",
      name: "Printing",
      selector: (row) => row.cardRarity,
      cell: (row) => {
        return (
          <SelectCardRarity
            isFormik={false}
            value={row.cardRarity}
            priceChartingProductInfo={row}
            tradeinMargin={tradeinMargin}
            markupPresets={markupPresets}
            setFieldValue={(...e) => {
              handleOnChangeCardRarityInBatchModalRow(e, row);
            }}
          />
        );
      },
      sortable: false,
      sortDirection: null,
      width: "8%",
    }
  );
};

// Same column used in inventory and invoice modals
const getBatchModalQuantityColumn = (
  currentStore,
  addBatchInventory,
  setAddBatchInventory
) => {
  return {
    id: "quantity",
    name: "Quantity",
    selector: (row) => getQuantityValue(row, false),
    cell: (row) => {
      return (
        <SimpleNumberField
          step={1}
          minimumValue={1}
          key={row.id}
          value={getQuantityValue(row, false)}
          isConvertToDecimal={false}
          className={`minimum-number-field max-input-width w-100 trade-batch-input-fields`}
          maxValue={inventoryConstants.THRESHOLD_MAX_CASH_VALUE}
          disabled={
            (isSystemProduct(getProductSubCategory(row?.genre)) &&
              currentStore[
                toggleSwitchButtonConstants.IS_SERIAL_NUMBER_REQUIRED_INVENTORY
              ]) ||
            row?.serialNumber ||
            row?.isItemLocked ||
            (!row.trackQuantity && isStoreCustomProduct(row, "apiSource"))
          }
          onChange={(value) => {
            if (value !== 0) {
              onChangeProductQuantity(
                row.id,
                value,
                addBatchInventory,
                setAddBatchInventory,
                handleCalculateEachProductPercentage
              );
            }
          }}
          isHandleKeyDown={true}
        />
      );
    },
    sortable: false,
    sortDirection: null,
    width: "8%",
  };
};

// Same column used in inventory and invoice modals
const getBatchModalCogsColumn = (
  addBatchInventory,
  setAddBatchInventory,
  handleEmptyOnFocus = () => {}
) => {
  return {
    id: "cogs",
    name: "COGS",
    selector: (row) => getCogsValue(row, false, false),
    cell: (row) => {
      return (
        <input
          key={getCogsValue(row, false, false)}
          min={0}
          type="number"
          defaultValue={getCogsValue(row, false, false)}
          onFocus={handleEmptyOnFocus}
          className="input-textfield editable-inventory-cell-max-size trade-batch-input-fields"
          onBlur={(e) => {
            const inputValue = parseToDecimalNumber(e.target.value); // Convert to number
            batchCostOfGoodsChange(
              row.id,
              inputValue,
              addBatchInventory,
              setAddBatchInventory
            );
          }}
          disabled={row?.isItemLocked}
        ></input>
      );
    },
    sortable: false,
    sortDirection: null,
    width: "8%",
  };
};

const getModalStockPriceColumn = (
  currentStore,
  addBatchInventory,
  setAddBatchInventory,
  productTags,
  handleEmptyOnFocus = () => {},
  markupPresets,
  columnOptions = {}
) => {
  return {
    id: "stockPrice",
    name: "Stock Price",
    selector: (row) => getInvStockPrice(row, false, false),
    cell: (row) => {
      return (
        <input
          key={getInvStockPrice(row, false, false)}
          min={0}
          type="number"
          // value={row.inStockPrice}
          defaultValue={getInvStockPrice(row, false, false)}
          onFocus={handleEmptyOnFocus}
          // onChange={(e) => onChangeStockPrice(e.target.value, row.id)}
          className={`input-textfield editable-inventory-cell-max-size trade-batch-input-fields ${
            isMarketPriceHigh(
              row.marketPrice,
              getInvStockPrice(row, false, false),
              productTags,
              row.tags.map((tag) => tag.label),
              row.isDataFetching
            )
              ? "border-danger"
              : ""
          }`}
          onBlur={(e) => {
            const inputValue = parseToDecimalNumber(e.target.value); // Convert to number
            const prevValue = parseToDecimalNumber(
              row?.isSeparateQuantity && row?.isOnlineUnitSellPrice
                ? row?.onlineUnitSellPrice
                : row.inStockPrice
            );
            inputValue !== prevValue &&
              batchStockPriceChange(
                row.id,
                inputValue,
                currentStore,
                markupPresets,
                addBatchInventory,
                setAddBatchInventory
              );
          }}
          disabled={row?.isItemLocked}
        ></input>
      );
    },
    sortable: false,
    sortDirection: null,
    ...columnOptions,
  };
};

// Same column used in inventory, invoice, and trade batch modal
const getBatchModalExistingStockColumn = (
  currentStore,
  markupPresets,
  addBatchInventory,
  setAddBatchInventory
) => {
  return {
    id: "inStockPriceAnd",
    name: "",
    cell: (row) => {
      const onChangeStockPrice = (value, id) =>
        batchStockPriceChange(
          id,
          value,
          currentStore,
          markupPresets,
          addBatchInventory,
          setAddBatchInventory
        );
      return getProductIfExist(row, onChangeStockPrice, "text-nowrap");
    },
    sortable: false,
  };
};

// Same column used in inventory, invoice, and trade batch modal
const getBatchModalActionColumn = (
  handleEditClick,
  handleDeleteClick,
  currentStore,
  addBatchInventory,
  setAddBatchInventory
) => {
  return getActionColumnOfReactDataTableComponent((row) => {
    return row?.isDataFetching ? (
      <div className="d-flex justify-content-center align-items-center">
        <RotatingLines />
      </div>
    ) : (
      <div className="d-flex justify-content-center align-items-center gap-2">
        {/* delete icon button */}
        <button
          id={`modalTool${row.id}`}
          className="action-Button-Wrapper  ps-0 pe-0"
          onClick={() =>
            handleDeleteClick(row.id, addBatchInventory, setAddBatchInventory)
          }
        >
          <DeleteIcon className="cursor-pointer text-primary" />
        </button>{" "}
        <button
          id={`modalTool${row.id}`}
          className="action-Button-Wrapper  ps-0 pe-0 mx-auto"
          onClick={() => {
            handleLockOrUnlockTradeItem(
              currentStore,
              addBatchInventory,
              setAddBatchInventory,
              row.id
            );
          }}
        >
          {row?.isItemLocked ? (
            <LockIcon className="cursor-pointer text-primary" />
          ) : (
            <UnLockIcon className="cursor-pointer " />
          )}
        </button>
        <button
          id={`modalTool${row.id}`}
          className="action-Button-Wrapper ps-0 pe-0"
          onClick={() => {
            handleEditClick(row);
          }}
        >
          <EditIcon className="cursor-pointer text-primary" />
        </button>
      </div>
    );
  });
};

const getAddBatchInventoryColumns = ({
  productTags,
  currentStore,
  markupPresets,
  tradeinMargin,
  addBatchInventory,
  handleEmptyOnFocus,
  handleEditCardRowClick,
  setAddBatchInventory,
  onChangeProductQuantity,
}) => {
  let columns = [
    {
      id: "title",
      name: "Title",
      selector: (row) => row.productName,
      cell: (row) => {
        return (
          <div className="d-flex flex-column">
            <TableCustomHeader
              id={`tool${row.id}`}
              label={
                <span className="inventory-cell-max-size product-title-max-width">
                  {row.productName}
                </span>
              }
              toolTipText={row.productName}
            />
            {getProductSubCategory(row.genre) ===
              TRADING_CARD_CATEGORIES_ENUMS.SYSTEM &&
              (currentStore[
                toggleSwitchButtonConstants.IS_SERIAL_NUMBER_REQUIRED_INVENTORY
              ] ||
                row?.serialNumber) && (
                <span
                  className={`${
                    row.serialNumber ? EMPTY_STRING : "text-danger"
                  } batch-seial-number-label`}
                >
                  S/N : {row?.serialNumber || "Required"}
                </span>
              )}
          </div>
        );
      },
      sortable: false,
      sortDirection: null,
    },
    getBatchModalGradeColumn(
      addBatchInventory,
      setAddBatchInventory,
      currentStore?.id,
      markupPresets,
      tradeinMargin,
      false
    ),
    {
      id: "condition",
      name: "Condition",
      selector: (row) => row.productCondition,
      cell: (row) => {
        return (
          <SelectDropDown
            menuPlacement="bottom"
            options={row.productConditionList}
            noOptionsMessage="No Type Found"
            placeHolder="Type"
            defaultPageSize={{
              value: row?.productCondition,
              label: handleShowNintendoCondition(
                row.consoleName,
                row?.productCondition
              ),
            }}
            handleChange={(e) =>
              onChangeProductCondition(
                markupPresets,
                row.id,
                e.value,
                false,
                addBatchInventory,
                setAddBatchInventory,
                tradeinMargin
              )
            }
            isDisabled={row?.isItemLocked}
            menuPortalTarget=""
            value={{
              value: row?.productCondition,
              label: handleShowNintendoCondition(
                row.consoleName,
                row?.productCondition
              ),
            }}
          />
        );
      },
      sortable: false,
      sortDirection: null,
    },
    getBatchModalRarityColumn(
      addBatchInventory,
      setAddBatchInventory,
      currentStore?.id,
      markupPresets,
      tradeinMargin
    ),
    {
      id: "type",
      name: "Type",
      selector: (row) => row.productType,
      cell: (row) => {
        return (
          <span className="inventory-cell-max-size">{row.productType}</span>
        );
      },
      sortable: false,
      sortDirection: null,
      width: "8%",
    },
    {
      id: "category",
      name: "Category",
      selector: (row) => row.consoleName,
      cell: (row) => {
        return (
          <span className="inventory-cell-max-size">{row.consoleName}</span>
        );
      },
      sortable: false,
      sortDirection: null,
    },
    {
      id: "marketPrice",
      name: "Market Price",
      selector: (row) => row.marketPrice,
      cell: (row) => {
        return (
          <span
            className={`inventory-cell-max-size ${
              isMarketPriceHigh(
                row.marketPrice,
                row.inStockPrice,
                productTags,
                row.tags.map((tag) => tag.label),
                row.isDataFetching
              )
                ? "text-danger"
                : ""
            }`}
          >
            {parseToThousandSeperatorDecimalNumber(row.marketPrice)}
          </span>
        );
      },
      sortable: false,
      sortDirection: null,
      width: "8%",
    },
    getBatchModalQuantityColumn(
      currentStore,
      addBatchInventory,
      setAddBatchInventory
    ),
    getBatchModalCogsColumn(
      addBatchInventory,
      setAddBatchInventory,
      handleEmptyOnFocus
    ),
    getModalStockPriceColumn(
      currentStore,
      addBatchInventory,
      setAddBatchInventory,
      productTags,
      handleEmptyOnFocus,
      markupPresets,
      { width: "8%" }
    ),
    getBatchModalExistingStockColumn(
      currentStore,
      markupPresets,
      addBatchInventory,
      setAddBatchInventory
    ),
    getBatchModalActionColumn(
      handleEditCardRowClick,
      handleBatchProductDelete,
      currentStore,
      addBatchInventory,
      setAddBatchInventory
    ),
  ];
  return columns;
};

export {
  getProductTagColumn,
  getProductNameColumn,
  getProductTypeColumn,
  getSkuColumnWithImage,
  getBatchModalGradeColumn,
  getProductCategoryColumn,
  getBatchModalRarityColumn,
  getProductConditionColumn,
  getBatchModalActionColumn,
  getAddBatchInventoryColumns,
  getBatchModalQuantityColumn,
  getBatchModalCogsColumn,
  getModalStockPriceColumn,
  getBatchModalExistingStockColumn,
};
